import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { rub } from '@/lib/currency';
import { useSelectedSubscription } from '@/lib/use-selected-subscription';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { periodMainNumber, periodMainUnit } from '@/lib/period';
import { getWebApp, haptic, openExternalUrl } from '@/lib/telegram';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Check, Sparkles, Globe2, ChevronRight, ShieldCheck, Zap, Rocket, Gem, Smartphone, BarChart3, Gift, Loader2, AlertTriangle, FlaskConical, Infinity as InfinityIcon, Megaphone, CalendarDays } from 'lucide-react';
import { GlyphChip, IconChip, SectionLabel } from '@/components/ui/calm';
import { cn } from '@/lib/utils';
import { track } from '@/lib/analytics';
import type { Plan } from '@/api/types';
import { SkeletonCard, SkeletonGrid } from '@/components/ui/skeleton';
import { Flag } from '@/components/ui/flag';

type Section = 'term' | 'lifetime';

export const Route = createFileRoute('/plans')({
  // `?section=lifetime` открывает раздел «Навсегда»: по этой ссылке ведут прогрев
  // пробника «Навсегда» и кнопки рассылок партнёра.
  validateSearch: (search: Record<string, unknown>): { section?: 'lifetime' } =>
    search.section === 'lifetime' ? { section: 'lifetime' } : {},
  component: PlansPage,
});

function PlansPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  // Раздел «Навсегда» есть не у всех ботов: без него экран выглядит как раньше.
  const lifetimeEnabled = api.getAuth().lifetimeEnabled;
  const section: Section = lifetimeEnabled && search.section === 'lifetime' ? 'lifetime' : 'term';
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [selectedDays, setSelectedDays] = useState<number | null>(null);
  // Покупка отсюда всегда создаёт новую подписку, а не продлевает текущую. Человек с
  // подпиской, зашедший «продлить», должен узнать об этом до оплаты, а не после.
  const { subs, sub: currentSub } = useSelectedSubscription();

  const { data: plans, isPending } = useQuery({
    queryKey: ['plans'],
    queryFn: () => api.plans(),
    placeholderData: keepPreviousData,
    // Цены перечитываем при каждом заходе на экран. Владелец зеркала менял наценку, а
    // открытый мини-апп держал старый список пять минут; чекаут при этом считал по
    // свежим ценам, и суммы расходились (тикет #12).
    staleTime: 0,
    refetchOnMount: 'always',
  });
  const {
    data: lifetimePlans,
    isPending: lifetimePending,
    isError: lifetimeFailed,
    refetch: refetchLifetime,
  } = useQuery({
    queryKey: ['plans', 'lifetime'],
    queryFn: () => api.plans('lifetime'),
    enabled: lifetimeEnabled,
    staleTime: 0,
    refetchOnMount: 'always',
  });
  const lifetimePlan = lifetimePlans?.[0] ?? null;

  // Спецтарифы (тест обхода белых списков) живут отдельной карточкой и НЕ участвуют
  // в сетке периодов: трёхдневный тест иначе добавил бы шестую кнопку срока и стал бы
  // базой расчёта выгоды, перекосив длинные периоды.
  const regularPlans = useMemo(() => (plans ?? []).filter((p) => !p.isSpecial), [plans]);
  const specialPlans = useMemo(() => (plans ?? []).filter((p) => p.isSpecial), [plans]);

  // Список доступных периодов из плана. Сортируем по возрастанию длины.
  const periods = useMemo(() => {
    const uniq = Array.from(new Set(regularPlans.map((p) => p.days))).sort((a, b) => a - b);
    return uniq;
  }, [regularPlans]);

  // Дефолтный выбор: 30 дней если есть, иначе самый короткий
  useEffect(() => {
    if (selectedDays === null && periods.length > 0) {
      setSelectedDays(periods.includes(30) ? 30 : periods[0]);
    }
  }, [periods, selectedDays]);

  const filtered = useMemo(() => {
    if (selectedDays === null) return [];
    return regularPlans
      .filter((p) => p.days === selectedDays)
      .sort((a, b) => a.devices - b.devices);
  }, [regularPlans, selectedDays]);

  // «Популярный» выбирает сервер (#214): партнёр указывает семейство в кабинете, по
  // умолчанию самое продаваемое у бота. Раньше бейдж всегда стоял на самом дорогом,
  // и партнёры просили это поменять. Одну карточку из одной не выделяем.
  const popularPlanId = useMemo(() => {
    if (filtered.length < 2) return null;
    return filtered.find((p) => p.tags.includes('popular'))?.id ?? null;
  }, [filtered]);

  useEffect(() => {
    track('plans_viewed', { count: plans?.length ?? 0 });
  }, [plans?.length]);

  useEffect(() => {
    const tg = getWebApp();
    if (!tg?.BackButton) return;
    tg.BackButton.show();
    const onBack = () => navigate({ to: '/' });
    tg.BackButton.onClick(onBack);
    return () => tg.BackButton.offClick(onBack);
  }, [navigate]);

  // Кнопку ведём от id и цены выбранного тарифа, а не от всего массива `plans`:
  // react-query отдаёт новый массив на каждый фоновый refetch, и эффект перезапускался
  // вхолостую. Каждый холостой прогон делал hide() в cleanup и show() заново — лишняя
  // пара событий в клиент Telegram, где кнопка обновляется асинхронно.
  // В разделе «Навсегда» тариф один, выбирать нечего: кнопка сразу на него.
  const mainPlan =
    section === 'lifetime' ? lifetimePlan : plans?.find((p) => p.id === selectedPlanId);
  const mainPlanId = mainPlan?.id ?? null;
  const mainPrice =
    mainPlan && mainPlan.discountedPriceRub != null && mainPlan.discountedPriceRub < mainPlan.priceRub
      ? mainPlan.discountedPriceRub
      : mainPlan?.priceRub;

  useEffect(() => {
    const tg = getWebApp();
    if (!tg?.MainButton) return;
    if (!mainPlanId || mainPrice === undefined) {
      tg.MainButton.hide();
      return;
    }
    // Текст и видимость одним вызовом: setText + show — это два события, между
    // которыми кнопка живёт в промежуточном состоянии.
    tg.MainButton.setParams({ text: t('Buy for price', { price: mainPrice }), is_visible: true });
    const onMain = () => navigate({ to: '/checkout/$planId', params: { planId: mainPlanId } });
    tg.MainButton.onClick(onMain);
    return () => {
      tg.MainButton.offClick(onMain);
      tg.MainButton.hide();
    };
  }, [mainPlanId, mainPrice, navigate]);

  const switchSection = (next: Section) => {
    haptic('light');
    setSelectedPlanId(null);
    navigate({ to: '/plans', search: next === 'lifetime' ? { section: 'lifetime' } : {}, replace: true });
  };

  if (section === 'lifetime') {
    return (
      <div className="p-4 space-y-5 pb-24">
        <div className="px-2 pt-2">
          <h2 className="text-[25px] font-extrabold tracking-[-0.8px] text-foreground">{t('Plans')}</h2>
          <p className="text-sm text-[#8a8c93] mt-1">{t('Lifetime hint')}</p>
        </div>
        <SectionTabs section={section} onChange={switchSection} />
        <TrialPromo />
        <LifetimeOffer
          plan={lifetimePlan}
          pending={lifetimePending && !lifetimePlans}
          failed={lifetimeFailed && !lifetimePlans}
          onRetry={() => refetchLifetime()}
          onTerm={() => switchSection('term')}
        />
        <LocationsPromo section="lifetime" />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-5 pb-24">
      <div className="px-2 pt-2">
        <h2 className="text-[25px] font-extrabold tracking-[-0.8px] text-foreground">{t('Plans')}</h2>
        <p className="text-sm text-[#8a8c93] mt-1">
          {t('Plans hint')}
        </p>
      </div>

      {lifetimeEnabled && <SectionTabs section={section} onChange={switchSection} />}

      <TrialPromo />

      {specialPlans.map((plan) => (
        <SpecialPlanPromo key={plan.id} plan={plan} />
      ))}

      <LocationsPromo />

      <div className="rounded-2xl calm-card p-3.5">
        <div className="flex items-center gap-3">
          <IconChip icon={ShieldCheck} tone="blue" size={40} />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-bold leading-tight text-foreground">
              {t('Plans bypass title')}
            </div>
            <div className="mt-0.5 text-xs text-[#8a8c93] leading-snug">
              {t('Plans bypass desc')}
            </div>
          </div>
        </div>
      </div>

      {subs.length > 0 && (
        <div className="rounded-2xl calm-card p-3.5 flex items-start gap-3">
          <IconChip icon={AlertTriangle} tone="amber" size={40} />
          <div className="min-w-0 flex-1 text-xs leading-snug text-[#8a8c93]">
            {t('Plans existing sub note')}
            {currentSub && !currentSub.isTrial && (
              <>
                {' '}
                <Link
                  to="/renew/$subscriptionId"
                  params={{ subscriptionId: currentSub.id }}
                  className="text-accent font-semibold"
                >
                  {t('Plans renew current')}
                </Link>
              </>
            )}
          </div>
        </div>
      )}

      {/* Шаг 1: период */}
      <div>
        <SectionLabel className="px-2 mb-2">{t('Plans step period')}</SectionLabel>
        {isPending && !plans ? (
          <SkeletonGrid
            count={5}
            cols={5}
            className="gap-2 px-1"
            itemClassName="h-[58px] min-h-0 rounded-[16px]"
          />
        ) : (
        <div className="grid grid-cols-5 gap-2 px-1">
          {periods.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => {
                setSelectedDays(d);
                setSelectedPlanId(null);
              }}
              className={cn(
                'py-3 rounded-[16px] text-center transition-all active:scale-95',
                selectedDays === d ? 'calm-cta' : 'calm-card text-foreground',
              )}
            >
              <div className="font-bold text-base leading-tight">{periodMainNumber(d)}</div>
              <div className={cn('text-[10px] mt-0.5', selectedDays === d ? 'opacity-90' : 'text-[#8a8c93]')}>
                {periodMainUnit(d)}
              </div>
            </button>
          ))}
        </div>
        )}
      </div>

      {/* Шаг 2: тариф. key по периоду — карточки переигрывают появление при смене
          срока, иначе цены молча подменяются под пальцем и это легко пропустить. */}
      {selectedDays !== null && (
        <div key={selectedDays} className="enter-soft">
          <SectionLabel className="px-2 mb-2">{t('Plans step plan')}</SectionLabel>
          {filtered.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center text-sm text-muted-foreground">
                {t('Plans empty')}
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {filtered.map((plan) => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  selected={selectedPlanId === plan.id}
                  popular={popularPlanId === plan.id}
                  onPick={() => {
                    setSelectedPlanId(plan.id);
                    track('plan_selected', {
                      plan_id: plan.id,
                      price_rub: plan.priceRub,
                      days: plan.days,
                      devices: plan.devices,
                    });
                  }}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PlanCard({
  plan,
  selected,
  popular,
  onPick,
}: {
  plan: Plan;
  selected: boolean;
  popular: boolean;
  onPick: () => void;
}) {
  const { t } = useTranslation();
  const hasDiscount =
    plan.discountedPriceRub != null && plan.discountedPriceRub < plan.priceRub;
  const effectivePrice = hasDiscount ? plan.discountedPriceRub! : plan.priceRub;
  const perDay = effectivePrice > 0 ? Math.max(1, Math.round(effectivePrice / plan.days)) : 0;
  const TierIcon = plan.devices <= 2 ? Zap : plan.devices <= 5 ? Rocket : Gem;
  return (
    <button
      type="button"
      onClick={onPick}
      className={cn(
        'relative w-full text-left rounded-[20px] border p-4 transition-all active:scale-[0.98] flex flex-col gap-3',
        selected
          ? 'border-accent bg-primary/8'
          : popular
            ? 'border-[rgba(78,164,240,0.4)] calm-card'
            : 'calm-card',
      )}
    >
      {popular && (
        <div className="absolute -top-2 left-3 calm-cta text-[9px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
          <Sparkles className="w-2.5 h-2.5" />
          {t('Popular')}
        </div>
      )}
      {selected && (
        <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-accent flex items-center justify-center">
          <Check className="w-4 h-4 text-white" strokeWidth={3} />
        </div>
      )}

      <div>
        {plan.emoji ? (
          <GlyphChip glyph={plan.emoji} size={40} />
        ) : (
          <IconChip icon={TierIcon} tone="blue" size={40} />
        )}
        <div className="font-bold text-base mt-2 leading-tight text-foreground">{plan.name}</div>
      </div>

      <div className="text-xs text-[#8a8c93] space-y-1">
        <div className="flex items-center gap-1.5">
          <Smartphone className="w-3.5 h-3.5" />
          {t('Count devices', { count: plan.devices })}
        </div>
        <div className="flex items-center gap-1.5 text-[#5fe3ad]">
          <InfinityIcon className="w-3.5 h-3.5" />
          {t('Unlimited everywhere')}
        </div>
        {plan.trafficGb !== null && (
          <div className="flex items-center gap-1.5 text-[10px] text-[#6b6d74]">
            <BarChart3 className="w-3 h-3" />
            {t('GB for bypass', { gb: plan.trafficGb })}
          </div>
        )}
      </div>

      <div className="mt-auto">
        {/* Персональная скидка (за отзыв или из предложения) применяется сама на
            оплате, но раньше список тарифов о ней молчал: человек видел старую цену
            и до чекаута, где она наконец появлялась, часто не доходил. */}
        {hasDiscount ? (
          <div className="flex items-baseline gap-1.5">
            <div className="text-[21px] font-extrabold leading-none tabular text-accent">
              {rub(plan.discountedPriceRub ?? 0)}
            </div>
            <div className="text-[13px] text-[#8a8c93] line-through tabular">{rub(plan.priceRub)}</div>
          </div>
        ) : (
          <div className="text-[21px] font-extrabold leading-none tabular text-foreground">
            {plan.priceRub === 0 ? i18n.t('Free price') : rub(plan.priceRub)}
          </div>
        )}
        {perDay > 0 && (
          <div className="text-[11px] text-[#8a8c93] mt-1">
            {t('Per day', { price: perDay })}
          </div>
        )}
      </div>
    </button>
  );
}

function TrialPromo() {
  const { data } = useQuery({
    queryKey: ['trial-status'],
    queryFn: () => api.trialStatus(),
    staleTime: 60_000,
  });
  if (!data?.available && !data?.lifetime_available) return null;
  const channels = (data.channels ?? []).filter((c) => /^(https:\/\/|tg:\/\/)/.test(c.url));
  if (channels.length === 0) return <TrialPromoCard data={data} />;
  return (
    <div className="space-y-2">
      <TrialChannels channels={channels} />
      <TrialPromoCard data={data} />
    </div>
  );
}

type TrialStatus = Awaited<ReturnType<typeof api.trialStatus>>;

/** Пробник только подписчикам канала (#227): каналы над карточкой пробника. Выдача
 *  проверяет подписку сама, здесь только ссылки, чтобы не гадать, куда вступать. */
function TrialChannels({ channels }: { channels: { url: string; name: string }[] }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-2xl calm-card p-3.5">
      <div className="text-[13px] font-bold text-foreground">{t('Trial channels title')}</div>
      <div className="mt-0.5 text-[11.5px] leading-snug text-[#8a8c93]">{t('Trial channels note')}</div>
      <div className="mt-2.5 grid gap-1.5">
        {channels.map((c) => (
          <button
            key={c.url}
            type="button"
            onClick={() => {
              haptic('light');
              openExternalUrl(c.url);
            }}
            className="flex min-h-11 w-full items-center gap-2 rounded-xl surface-inset px-3 text-left text-[13px] font-semibold text-foreground active:scale-[0.99] transition-transform"
          >
            <Megaphone className="h-4 w-4 shrink-0 text-primary" strokeWidth={2} />
            <span className="min-w-0 flex-1 truncate">{c.name || c.url.replace(/^https:\/\//, '')}</span>
            <ChevronRight className="h-4 w-4 shrink-0 text-[#8a8c93]" />
          </button>
        ))}
      </div>
    </div>
  );
}

function TrialPromoCard({ data }: { data: TrialStatus }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const claim = useMutation({
    mutationFn: (section: Section) => api.claimTrial(section),
    onSuccess: () => {
      haptic('success');
      qc.invalidateQueries({ queryKey: ['trial-status'] });
      qc.invalidateQueries({ queryKey: ['subs'] });
      toast.success(t('Trial activated'));
      navigate({ to: '/me' });
    },
    onError: (e: Error) => {
      haptic('error');
      const code = (e as { body?: { error?: { code?: string } } }).body?.error?.code;
      const msg =
        code === 'app.trial.channel_required'
          ? t('Trial channel required')
          : code === 'app.trial.already_used'
          ? t('Trial already used')
          : code === 'app.trial.disabled'
            ? t('Trial unavailable')
            : code === 'app.trial.no_plan'
              ? t('Trial plan unavailable')
              : t('Trial failed');
      toast.error(msg);
      qc.invalidateQueries({ queryKey: ['trial-status'] });
    },
  });

  const lifetimeTrial = Boolean(data?.lifetime_available);
  if (!data?.available && !lifetimeTrial) return null;

  // Пробник один на человека, и выбор надо объяснить до нажатия: взяв один, второй
  // уже не получить (решение Глеба: «выбирайте подписку, которую будете юзать»).
  if (lifetimeTrial) {
    const busy = claim.isPending;
    return (
      <div className="rounded-2xl p-4 border border-[rgba(56,224,165,0.28)] [background:linear-gradient(150deg,rgba(56,224,165,0.16),rgba(56,224,165,0.02)_55%,#141517)]">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[rgba(56,224,165,0.18)] text-money flex items-center justify-center shrink-0">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Gift className="w-5 h-5" />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-bold leading-tight text-foreground">{t('Try free')}</div>
            {/* «Выберите один из двух» верно, только когда пробников два: партнёр мог
                выключить пробник на срок (заявка #160). */}
            <div className="mt-0.5 text-xs text-[#8a8c93] leading-snug">
              {data?.available ? t('Trial choose note') : t('Trial note')}
            </div>
          </div>
        </div>
        <div className="mt-3 grid gap-2">
          {data?.available && (
            <button
              type="button"
              disabled={busy}
              onClick={() => claim.mutate('term')}
              className="w-full text-left rounded-xl calm-card px-3 py-2.5 active:scale-[0.99] transition-transform disabled:opacity-60"
            >
              <div className="text-[13px] font-bold text-foreground flex items-center gap-1.5">
                <CalendarDays className="w-3.5 h-3.5 shrink-0 text-primary" aria-hidden />
                {t('Trial term option', { days: t('Count days', { count: data.days }) })}
              </div>
              <div className="mt-0.5 text-[11px] leading-snug text-[#8a8c93]">{t('Trial term option note')}</div>
            </button>
          )}
          <button
            type="button"
            disabled={busy}
            onClick={() => claim.mutate('lifetime')}
            className="w-full text-left rounded-xl calm-card px-3 py-2.5 active:scale-[0.99] transition-transform disabled:opacity-60"
          >
            <div className="text-[13px] font-bold text-foreground flex items-center gap-1.5">
              <InfinityIcon className="w-3.5 h-3.5 shrink-0 text-primary" aria-hidden />
              {t('Trial lifetime option', { days: t('Count days', { count: data?.lifetime_days ?? 0 }) })}
            </div>
            <div className="mt-0.5 text-[11px] leading-snug text-[#8a8c93]">{t('Trial lifetime option note')}</div>
          </button>
        </div>
      </div>
    );
  }

  if (!data?.available) return null;
  const daysLabel = data.days > 0 ? t('Count days', { count: data.days }) : '';

  return (
    <button
      type="button"
      onClick={() => claim.mutate('term')}
      disabled={claim.isPending}
      className="block w-full text-left rounded-2xl p-4 active:scale-[0.99] transition-transform border border-[rgba(56,224,165,0.28)] [background:linear-gradient(150deg,rgba(56,224,165,0.16),rgba(56,224,165,0.02)_55%,#141517)] disabled:opacity-60"
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-[rgba(56,224,165,0.18)] text-money flex items-center justify-center shrink-0">
          {claim.isPending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Gift className="w-5 h-5" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold leading-tight text-foreground">
            {daysLabel ? t('Try free days', { days: daysLabel }) : t('Try free')}
          </div>
          <div className="mt-0.5 text-xs text-[#8a8c93] leading-snug">
            {t('Trial note')}
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-[#8a8c93] shrink-0" />
      </div>
      {/* Ожидания честнее задать до активации: в триале нет серверов обхода
          белых списков, и человек, который берёт его именно ради этого, иначе
          решит, что сервис не работает. */}
      <div className="mt-3 flex gap-2 rounded-xl bg-[rgba(245,166,35,0.08)] border border-[rgba(245,166,35,0.2)] px-3 py-2.5 text-[11px] leading-snug text-[#d6b88a]">
        <AlertTriangle className="w-3.5 h-3.5 mt-px shrink-0" strokeWidth={2} />
        <span>
          {t('Trial bypass note')}
        </span>
      </div>
    </button>
  );
}

/** Переключатель разделов «На срок / Навсегда». */
function SectionTabs({ section, onChange }: { section: Section; onChange: (s: Section) => void }) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-2 gap-1 rounded-[16px] calm-card p-1" role="tablist">
      {(['term', 'lifetime'] as const).map((s) => (
        <button
          key={s}
          type="button"
          role="tab"
          aria-selected={section === s}
          onClick={() => section !== s && onChange(s)}
          className={cn(
            'min-h-[44px] rounded-[12px] text-sm font-bold transition-colors',
            section === s ? 'calm-cta' : 'text-[#8a8c93]',
          )}
        >
          {s === 'term' ? t('Section term') : t('Section lifetime')}
        </button>
      ))}
    </div>
  );
}

/**
 * Раздел «Навсегда»: один тариф, платят один раз.
 *
 * Сетки периодов, «−N %» и цены за день здесь нет: срока у тарифа нет вовсе. Что
 * входит, пишем прямо в карточке: обход белых списков с месячным лимитом и докупки
 * человек должен понять до оплаты, а не из поддержки.
 */
function LifetimeOffer({
  plan,
  pending,
  failed,
  onRetry,
  onTerm,
}: {
  plan: Plan | null;
  pending: boolean;
  failed: boolean;
  onRetry: () => void;
  onTerm: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  if (pending) return <SkeletonCard className="rounded-[20px] h-[340px]" />;
  if (failed) {
    return (
      <Card>
        <CardContent className="p-6 text-center space-y-3">
          <div className="text-sm text-muted-foreground">{t('Lifetime load failed')}</div>
          <button type="button" onClick={onRetry} className="text-sm font-semibold text-accent min-h-[44px]">
            {t('Retry')}
          </button>
        </CardContent>
      </Card>
    );
  }
  if (!plan) {
    return (
      <Card>
        <CardContent className="p-6 text-center space-y-3">
          <div className="text-sm text-muted-foreground">{t('Lifetime unavailable')}</div>
          <button type="button" onClick={onTerm} className="text-sm font-semibold text-accent min-h-[44px]">
            {t('Show term plans')}
          </button>
        </CardContent>
      </Card>
    );
  }
  const hasDiscount = plan.discountedPriceRub != null && plan.discountedPriceRub < plan.priceRub;
  const features: { icon: typeof Smartphone; text: string }[] = [
    { icon: InfinityIcon, text: t('Lifetime no expiry') },
    { icon: Smartphone, text: t('Lifetime devices', { count: plan.devices }) },
    { icon: Sparkles, text: t('Lifetime own network') },
    { icon: Globe2, text: t('Lifetime servers unlimited') },
    ...(plan.trafficGb !== null
      ? [{ icon: ShieldCheck, text: t('Lifetime bypass traffic', { gb: plan.trafficGb }) }]
      : []),
    { icon: BarChart3, text: t('Lifetime addons note') },
  ];
  return (
    <div className="rounded-[20px] border border-[rgba(78,164,240,0.4)] calm-card p-4 space-y-4">
      <div className="flex items-center gap-3">
        <IconChip icon={InfinityIcon} tone="blue" size={44} />
        <div className="min-w-0 flex-1">
          <div className="font-extrabold text-lg leading-tight text-foreground">{t('Lifetime title')}</div>
          <div className="text-xs text-[#8a8c93] mt-0.5">{t('Lifetime once note')}</div>
        </div>
      </div>
      <ul className="space-y-2">
        {features.map(({ icon: Icon, text }) => (
          <li key={text} className="flex items-start gap-2 text-[13px] leading-snug text-foreground">
            <Icon className="w-4 h-4 mt-0.5 shrink-0 text-accent" />
            <span>{text}</span>
          </li>
        ))}
      </ul>
      <div className="flex items-end justify-between gap-3 pt-1">
        {hasDiscount ? (
          <div className="flex items-baseline gap-1.5">
            <div className="text-[26px] font-extrabold leading-none tabular text-accent">
              {rub(plan.discountedPriceRub ?? 0)}
            </div>
            <div className="text-[13px] text-[#8a8c93] line-through tabular">{rub(plan.priceRub)}</div>
          </div>
        ) : (
          <div className="text-[26px] font-extrabold leading-none tabular text-foreground">
            {rub(plan.priceRub)}
          </div>
        )}
        <button
          type="button"
          onClick={() => {
            haptic('light');
            track('plan_selected', { plan_id: plan.id, price_rub: plan.priceRub, lifetime: true });
            navigate({ to: '/checkout/$planId', params: { planId: plan.id } });
          }}
          className="calm-cta rounded-[14px] px-4 min-h-[44px] text-sm font-bold active:scale-[0.98] transition-transform"
        >
          {t('Lifetime get')}
        </button>
      </div>
    </div>
  );
}

/**
 * Спецтариф: короткий платный тест обхода белых списков.
 *
 * Отдельной карточкой, а не в общей сетке, по двум причинам. Продуктовая: это не
 * «ещё один тариф», а разовая проверка, работает ли обход у конкретного человека.
 * Техническая: в сетке периодов трёхдневный план стал бы базой расчёта выгоды и
 * перекосил бы проценты на длинных сроках.
 *
 * Цену не форматируем из наценки: она жёстко задана владельцем и одинакова у всех
 * партнёров, поэтому берём `priceRub` как есть.
 */
function SpecialPlanPromo({ plan }: { plan: Plan }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const used = plan.alreadyPurchased;

  return (
    <button
      type="button"
      disabled={used}
      onClick={() => {
        haptic('light');
        navigate({ to: '/checkout/$planId', params: { planId: plan.id } });
      }}
      className={cn(
        'block w-full text-left rounded-2xl p-4 transition-transform border',
        used
          ? 'border-white/[0.06] bg-[#141517] opacity-60'
          : 'active:scale-[0.99] border-[rgba(90,160,255,0.28)] [background:linear-gradient(150deg,rgba(90,160,255,0.16),rgba(90,160,255,0.02)_55%,#141517)]',
      )}
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-[rgba(90,160,255,0.18)] text-[#5aa0ff] flex items-center justify-center shrink-0">
          <FlaskConical className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold leading-tight text-foreground">
            {used
              ? t('Whitelist test done')
              : t('Whitelist test buy', { price: plan.priceRub })}
          </div>
          <div className="mt-0.5 text-xs text-[#8a8c93] leading-snug">
            {plan.trafficGb
              ? t('Whitelist test sub gb', {
                  days: t('Count days', { count: plan.days }),
                  devices: t('Count devices', { count: plan.devices }),
                  gb: plan.trafficGb,
                })
              : t('Whitelist test sub', {
                  days: t('Count days', { count: plan.days }),
                  devices: t('Count devices', { count: plan.devices }),
                })}
          </div>
        </div>
        {!used && <ChevronRight className="w-4 h-4 text-[#8a8c93] shrink-0" />}
      </div>
      <div className="mt-3 flex gap-2 rounded-xl bg-[rgba(245,166,35,0.08)] border border-[rgba(245,166,35,0.2)] px-3 py-2.5 text-[11px] leading-snug text-[#d6b88a]">
        <AlertTriangle className="w-3.5 h-3.5 mt-px shrink-0" strokeWidth={2} />
        <span>
          {used
            ? t('Whitelist used note')
            : t('Whitelist scarce note')}
        </span>
      </div>
    </button>
  );
}

function LocationsPromo({ section = 'term' }: { section?: Section }) {
  const { t } = useTranslation();
  const { data } = useQuery({
    queryKey: ['client-locations', section],
    queryFn: () => api.locations(section),
    staleTime: 5 * 60 * 1000,
  });
  const total = data?.total ?? 0;
  if (total === 0) return null;
  const preview = Object.values(data?.regions ?? {})
    .flat()
    .slice(0, 6);
  return (
    <Link
      to="/locations"
      search={{ section }}
      preload="intent"
      className="block rounded-2xl calm-card p-3.5 active:scale-[0.99] transition-transform"
    >
      <div className="flex items-center gap-3">
        <IconChip icon={Globe2} tone="blue" size={40} />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold leading-tight text-foreground">
            {t('Plans locations note', { count: t('Count locations', { count: total }) })}
          </div>
          <div className="mt-1 flex items-center gap-1">
            {preview.map((loc, i) => (
              <span key={i} className="text-base leading-none">
                <Flag flag={loc.flag} code={loc.country_code} />
              </span>
            ))}
            <span className="text-[10px] text-[#8a8c93] ml-1 font-semibold">{t('More')}</span>
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-[#8a8c93] shrink-0" />
      </div>
    </Link>
  );
}
